import { VariableResolver } from './variableResolver';
import {
  decompose,
  isShorthandProperty,
  type ShorthandPropertyName,
} from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';

/**
 * Neutral tokens for each shorthand property type.
 * Used when substituting unresolved var() references.
 */
const NEUTRAL_TOKENS: Record<ShorthandPropertyName, string> = {
  // Length-based properties use 0px
  padding: '0px',
  gap: '0px',
  borderRadius: '0px',
  borderWidth: '0px',

  // Color-based properties use transparent
  borderColor: 'transparent',

  // Style-based properties use solid
  borderStyle: 'solid',

  // Mixed shorthands: border needs width/style/color
  border: 'transparent', // color slot is most likely to have var()
  borderTop: 'transparent',
  borderRight: 'transparent',
  borderBottom: 'transparent',
  borderLeft: 'transparent',
  borderInlineStart: 'transparent',
  borderInlineEnd: 'transparent',

  // Text properties
  font: '16px',
};

/**
 * Empty/default values for each shorthand property type.
 * Used when value is completely unparseable.
 */
const EMPTY_VALUES: Record<ShorthandPropertyName, string> = {
  // Length-based properties use 0px
  padding: '0px',
  gap: '0px',
  borderRadius: '0px',
  borderWidth: '0px',

  // Color-based properties use transparent
  borderColor: 'transparent',

  // Style-based properties use none
  borderStyle: 'none',

  // Mixed shorthands: complete empty value (width style color)
  border: '0px none transparent',
  borderTop: '0px none transparent',
  borderRight: '0px none transparent',
  borderBottom: '0px none transparent',
  borderLeft: '0px none transparent',
  borderInlineStart: '0px none transparent',
  borderInlineEnd: '0px none transparent',

  // Text properties
  font: '16px serif',
};

/** Mitigation rule: match condition and value transformation. */
type MitigationRule = {
  match: (propertyType: string, value: string) => boolean;
  mitigate: (
    value: string,
    neutralToken: string,
    themeVariables: Record<string, string>,
  ) => string;
};

/**
 * Wraps the decompose function with automatic mitigation for problematic values.
 * Checks values BEFORE parsing to avoid double decomposition.
 */
export class StyleInvalidValuesMitigator {
  private readonly themeVariables: Record<string, string>;

  /** Extensible list of mitigation rules, applied in order. */
  private readonly rules: MitigationRule[] = [
    // Rule 1: Embedded CSS variables in shorthand (e.g. "1px solid var(--c)")
    {
      match: (propertyType, value) =>
        isShorthandProperty(propertyType) &&
        value.includes('var(--') &&
        !VariableResolver.isSingleCSSVariable(value),
      mitigate: (value, neutralToken, themeVariables) =>
        this.substituteEmbeddedVars(value, neutralToken, themeVariables),
    },
  ];

  constructor(themeVariables: Record<string, string>) {
    this.themeVariables = themeVariables;
  }

  /**
   * Decompose with automatic mitigation for embedded CSS variables.
   * Uses property-specific neutral token for substitution.
   * Returns { [propertyType]: '' } for unparseable values to keep them as atomic.
   */
  decompose(
    propertyType: string,
    value: string,
  ): Record<string, string | null> | null {
    // Apply mitigation rules
    for (const rule of this.rules) {
      if (rule.match(propertyType, value)) {
        const neutralToken =
          NEUTRAL_TOKENS[propertyType as ShorthandPropertyName];
        const mitigated = rule.mitigate(
          value,
          neutralToken,
          this.themeVariables,
        );
        return decompose(propertyType, mitigated);
      }
    }

    // Try normal decomposition
    const result = decompose(propertyType, value);

    // If result has all nulls (unparseable), use empty value for this property type
    if (result && !this.hasNonNullValues(result)) {
      const emptyValue = EMPTY_VALUES[propertyType as ShorthandPropertyName];
      if (emptyValue) {
        return decompose(propertyType, emptyValue);
      }
    }

    return result;
  }

  /** Checks if decomposition result has at least one non-null value. */
  private hasNonNullValues(result: Record<string, string | null>): boolean {
    return Object.values(result).some((v) => v !== null);
  }

  /** Replaces embedded var() with resolved values or the given neutral token. */
  private substituteEmbeddedVars(
    value: string,
    neutralToken: string,
    themeVariables: Record<string, string>,
  ): string {
    return value.replace(/var\(--([^,\s)]+)\)/g, (_match, varName: string) => {
      if (themeVariables[varName] !== undefined) {
        return themeVariables[varName];
      }
      return neutralToken;
    });
  }
}
